<template>
  <section 
    id="video-section" 
    class="video-section"
    :aria-label="$t('video.sectionLabel')"
  >
    <div class="video-section__container">
      <!-- Section Header -->
      <div class="video-section__header">
        <h2 class="video-section__title">
          {{ content.videoTitle || $t('video.defaultTitle') }}
        </h2>
        <p 
          class="video-section__subtitle"
          v-if="content.videoSubtitle"
        >
          {{ content.videoSubtitle }}
        </p>
      </div>

      <!-- Main Video Player -->
      <div class="video-section__main-video">
        <div class="video-player" :class="{ 'video-player--loading': isLoading }">
          <!-- Video Element -->
          <video
            ref="videoElement"
            class="video-player__video"
            :poster="currentVideo.poster || content.videoPoster"
            :aria-label="currentVideo.title"
            @loadstart="handleLoadStart"
            @loadeddata="handleLoadedData"
            @play="handlePlay"
            @pause="handlePause"
            @ended="handleEnded"
            @timeupdate="handleTimeUpdate"
            @error="handleError"
            preload="metadata"
          >
            <source :src="currentVideo.url" :type="currentVideo.type || 'video/mp4'">
            <track
              v-if="currentVideo.subtitles"
              kind="subtitles"
              :src="currentVideo.subtitles"
              :srclang="currentCulture"
              :label="$t('video.subtitles')"
              default
            >
            <!-- Fallback for browsers that don't support video -->
            <p>{{ $t('video.notSupported') }}</p>
          </video>

          <!-- Custom Controls -->
          <div class="video-player__controls" v-show="!isLoading">
            <!-- Play/Pause Button -->
            <button
              class="video-player__control video-player__play-pause"
              @click="togglePlayPause"
              :aria-label="isPlaying ? $t('video.pause') : $t('video.play')"
            >
              <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            </button>

            <!-- Progress Bar -->
            <div class="video-player__progress-container">
              <div
                class="video-player__progress"
                @click="handleProgressClick"
                role="slider"
                :aria-valuemin="0"
                :aria-valuemax="duration"
                :aria-valuenow="currentTime"
                :aria-label="$t('video.progressLabel')"
                tabindex="0"
                @keydown="handleProgressKeydown"
              >
                <div 
                  class="video-player__progress-bar"
                  :style="{ width: progressPercentage + '%' }"
                ></div>
                <div 
                  class="video-player__progress-handle"
                  :style="{ left: progressPercentage + '%' }"
                ></div>
              </div>
              <div class="video-player__time">
                <span>{{ formatTime(currentTime) }}</span>
                <span>/</span>
                <span>{{ formatTime(duration) }}</span>
              </div>
            </div>

            <!-- Volume Control -->
            <div class="video-player__volume">
              <button
                class="video-player__control video-player__mute"
                @click="toggleMute"
                :aria-label="isMuted ? $t('video.unmute') : $t('video.mute')"
              >
                <svg v-if="!isMuted && volume > 0.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
                <svg v-else-if="!isMuted && volume > 0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              </button>
            </div>

            <!-- Fullscreen Button -->
            <button
              class="video-player__control video-player__fullscreen"
              @click="toggleFullscreen"
              :aria-label="$t('video.fullscreen')"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
              </svg>
            </button>
          </div>

          <!-- Loading Overlay -->
          <div class="video-player__loading" v-show="isLoading">
            <div class="video-player__spinner"></div>
            <p>{{ $t('video.loading') }}</p>
          </div>

          <!-- Error Overlay -->
          <div class="video-player__error" v-show="hasError">
            <div class="video-player__error-icon">⚠️</div>
            <p>{{ $t('video.error') }}</p>
            <button 
              class="video-player__retry"
              @click="retryVideo"
            >
              {{ $t('video.retry') }}
            </button>
          </div>
        </div>

        <!-- Video Info -->
        <div class="video-section__video-info">
          <h3 class="video-section__video-title">
            {{ currentVideo.title }}
          </h3>
          <p class="video-section__video-description">
            {{ currentVideo.description }}
          </p>
        </div>
      </div>

      <!-- Video Playlist -->
      <div 
        class="video-section__playlist"
        v-if="displayVideos.length > 1"
      >
        <h3 class="video-section__playlist-title">
          {{ content.playlistTitle || $t('video.moreVideos') }}
        </h3>
        <div class="video-playlist">
          <div
            v-for="(video, index) in displayVideos"
            :key="`video-${index}`"
            class="video-playlist__item"
            :class="{ 'video-playlist__item--active': currentVideoIndex === index }"
            @click="selectVideo(index)"
            @keydown.enter="selectVideo(index)"
            @keydown.space.prevent="selectVideo(index)"
            tabindex="0"
            role="button"
            :aria-label="`${$t('video.selectVideo')} ${video.title}`"
          >
            <div class="video-playlist__thumbnail">
              <img
                :src="video.thumbnail"
                :alt="video.title"
                loading="lazy"
              >
              <div class="video-playlist__play-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            <div class="video-playlist__info">
              <h4 class="video-playlist__title">{{ video.title }}</h4>
              <p class="video-playlist__duration">{{ formatTime(video.duration) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

interface Video {
  id: string
  title: string
  description: string
  url: string
  type?: string
  poster?: string
  thumbnail: string
  duration: number
  subtitles?: string
}

interface VideoContent {
  videoTitle?: string
  videoSubtitle?: string
  videoPoster?: string
  playlistTitle?: string
  videos?: Video[]
}

// Props
interface Props {
  content: VideoContent
}

const props = withDefaults(defineProps<Props>(), {
  content: () => ({})
})

// Composables
const { t } = useI18n()
const { locale } = useI18n()

// Reactive data
const videoElement = ref<HTMLVideoElement>()
const isLoading = ref(true)
const hasError = ref(false)
const isPlaying = ref(false)
const isMuted = ref(false)
const volume = ref(1)
const currentTime = ref(0)
const duration = ref(0)
const currentVideoIndex = ref(0)

// Computed properties
const currentCulture = computed(() => locale.value)

const displayVideos = computed<Video[]>(() => {
  const defaultVideos: Video[] = [
    {
      id: 'gameplay-1',
      title: t('video.defaultVideos.gameplay1.title'),
      description: t('video.defaultVideos.gameplay1.description'),
      url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
      thumbnail: 'https://picsum.photos/320/180?random=7',
      duration: 596
    },
    {
      id: 'gameplay-2',
      title: t('video.defaultVideos.gameplay2.title'),
      description: t('video.defaultVideos.gameplay2.description'),
      url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
      thumbnail: 'https://picsum.photos/320/180?random=8',
      duration: 653
    },
    {
      id: 'gameplay-3',
      title: t('video.defaultVideos.gameplay3.title'),
      description: t('video.defaultVideos.gameplay3.description'),
      url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
      thumbnail: 'https://picsum.photos/320/180?random=9',
      duration: 15
    }
  ]

  return props.content.videos || defaultVideos
})

const currentVideo = computed(() => displayVideos.value[currentVideoIndex.value] || displayVideos.value[0])

const progressPercentage = computed(() => {
  if (duration.value === 0) return 0
  return (currentTime.value / duration.value) * 100
})

// Methods
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const togglePlayPause = () => {
  if (!videoElement.value) return
  
  if (isPlaying.value) {
    videoElement.value.pause()
  } else {
    videoElement.value.play()
  }
}

const toggleMute = () => {
  if (!videoElement.value) return
  
  isMuted.value = !isMuted.value
  videoElement.value.muted = isMuted.value
}

const toggleFullscreen = () => {
  if (!videoElement.value) return
  
  if (document.fullscreenElement) {
    document.exitFullscreen()
  } else {
    videoElement.value.requestFullscreen()
  }
}

const selectVideo = (index: number) => {
  if (index === currentVideoIndex.value) return
  
  currentVideoIndex.value = index
  loadCurrentVideo()
}

const loadCurrentVideo = () => {
  if (!videoElement.value) return
  
  isLoading.value = true
  hasError.value = false
  isPlaying.value = false
  
  videoElement.value.src = currentVideo.value.url
  videoElement.value.load()
}

const retryVideo = () => {
  hasError.value = false
  loadCurrentVideo()
}

const handleProgressClick = (event: MouseEvent) => {
  if (!videoElement.value) return
  
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  const percentage = (event.clientX - rect.left) / rect.width
  videoElement.value.currentTime = percentage * duration.value
}

const handleProgressKeydown = (event: KeyboardEvent) => {
  if (!videoElement.value) return
  
  let step = 5 // 5 seconds
  if (event.shiftKey) step = 30 // 30 seconds with shift
  
  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault()
      videoElement.value.currentTime = Math.max(0, currentTime.value - step)
      break
    case 'ArrowRight':
      event.preventDefault()
      videoElement.value.currentTime = Math.min(duration.value, currentTime.value + step)
      break
  }
}

// Event handlers
const handleLoadStart = () => {
  isLoading.value = true
  hasError.value = false
}

const handleLoadedData = () => {
  isLoading.value = false
  if (videoElement.value) {
    duration.value = videoElement.value.duration
  }
}

const handlePlay = () => {
  isPlaying.value = true
}

const handlePause = () => {
  isPlaying.value = false
}

const handleEnded = () => {
  isPlaying.value = false
  // Auto-play next video if available
  if (currentVideoIndex.value < displayVideos.value.length - 1) {
    selectVideo(currentVideoIndex.value + 1)
  }
}

const handleTimeUpdate = () => {
  if (videoElement.value) {
    currentTime.value = videoElement.value.currentTime
  }
}

const handleError = () => {
  isLoading.value = false
  hasError.value = true
  isPlaying.value = false
}

// Lifecycle
onMounted(() => {
  loadCurrentVideo()
})

onUnmounted(() => {
  if (videoElement.value) {
    videoElement.value.pause()
  }
})
</script>

<style lang="scss" scoped>
.video-section {
  padding: 100px 0;
  background: #1a1a1a;
  color: white;
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 0;
  }

  &__container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  &__header {
    text-align: center;
    margin-bottom: 60px;

    @media (max-width: 768px) {
      margin-bottom: 40px;
    }
  }

  &__title {
    font-size: 3rem;
    font-weight: 800;
    color: white;
    margin-bottom: 24px;
    line-height: 1.1;
    position: relative;
    padding-bottom: 12px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      font-size: 2.25rem;
      margin-bottom: 20px;
    }
  }

  &__subtitle {
    font-size: 1.25rem;
    color: #cccccc;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }

  &__main-video {
    margin-bottom: 60px;

    @media (max-width: 768px) {
      margin-bottom: 40px;
    }
  }

  &__video-info {
    text-align: center;
    margin-top: 24px;
  }

  &__video-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    margin-bottom: 12px;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  &__video-description {
    font-size: 1rem;
    color: #cccccc;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 768px) {
      font-size: 0.9375rem;
    }
  }

  &__playlist-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    margin-bottom: 32px;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.25rem;
      margin-bottom: 24px;
    }
  }
}

// Video Player
.video-player {
  position: relative;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16/9;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.3);

  &__video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    opacity: 0;
    transition: opacity 0.3s ease;

    @media (max-width: 768px) {
      padding: 16px;
      gap: 12px;
    }
  }

  &:hover &__controls {
    opacity: 1;
  }

  &__control {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    svg {
      width: 24px;
      height: 24px;

      @media (max-width: 768px) {
        width: 20px;
        height: 20px;
      }
    }
  }

  &__progress-container {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__progress {
    flex: 1;
    height: 6px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
    position: relative;
    cursor: pointer;

    &:focus {
      outline: 2px solid #ff6b35;
      outline-offset: 2px;
    }
  }

  &__progress-bar {
    height: 100%;
    background: #ff6b35;
    border-radius: 3px;
    transition: width 0.1s ease;
  }

  &__progress-handle {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    background: #ff6b35;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &__progress:hover &__progress-handle {
    opacity: 1;
  }

  &__time {
    font-size: 0.875rem;
    color: white;
    white-space: nowrap;
    min-width: 80px;

    @media (max-width: 768px) {
      font-size: 0.8125rem;
      min-width: 70px;
    }
  }

  &__loading,
  &__error {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.8);
    color: white;
  }

  &__spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid #ff6b35;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  &__error-icon {
    font-size: 3rem;
    margin-bottom: 16px;
  }

  &__retry {
    background: #ff6b35;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 16px;
    transition: background 0.3s ease;

    &:hover {
      background: #ff8c42;
    }
  }
}

// Video Playlist
.video-playlist {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  &__item {
    display: flex;
    gap: 16px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: translateY(-2px);
    }

    &:focus {
      outline: none;
      border-color: #ff6b35;
    }

    &--active {
      background: rgba(255, 107, 53, 0.2);
      border-color: #ff6b35;
    }

    @media (max-width: 768px) {
      padding: 12px;
      gap: 12px;
    }
  }

  &__thumbnail {
    position: relative;
    width: 120px;
    height: 68px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    @media (max-width: 768px) {
      width: 100px;
      height: 56px;
    }
  }

  &__play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    color: white;
    opacity: 0.8;

    svg {
      width: 100%;
      height: 100%;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: white;
    margin-bottom: 8px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    @media (max-width: 768px) {
      font-size: 0.9375rem;
      margin-bottom: 6px;
    }
  }

  &__duration {
    font-size: 0.875rem;
    color: #cccccc;

    @media (max-width: 768px) {
      font-size: 0.8125rem;
    }
  }
}

// Animations
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
